/**
 * Decision Inbox — filterable, paginated read-only list of agent decisions.
 *
 * Reads GET /api/agentic/decisions (backend/app/routes/agentic_decisions.py).
 * Filters (risk / phase / agent) and page live in the URL query string so the
 * view is shareable and back-button friendly. Rows link to the decision card
 * (/decisions/:id). On 403/404 from the entitlement gate → NotEnabled.
 */
import { useSearchParams, Link } from 'react-router';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { AlertTriangle, ChevronRight, Inbox } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Heading,
  Icon,
  PageHeader,
  Section,
  Spinner,
  Stack,
  Text,
} from '@smms/design-system';
import { getDecisions, type RiskClassification } from '../api/agentic';
import { isNotEntitled } from '../api/client';
import NotEnabled from '../components/NotEnabled';

const PAGE_SIZE = 25;

const RISK_BADGE: Record<RiskClassification, 'success' | 'warning' | 'danger'> = {
  routine: 'success',
  non_routine: 'warning',
  safety_critical: 'danger',
};

const RISK_LABEL: Record<RiskClassification, string> = {
  routine: 'Routine',
  non_routine: 'Non-routine',
  safety_critical: 'Safety-critical',
};

const RISK_OPTIONS: { value: '' | RiskClassification; label: string }[] = [
  { value: '', label: 'All risk levels' },
  { value: 'routine', label: 'Routine' },
  { value: 'non_routine', label: 'Non-routine' },
  { value: 'safety_critical', label: 'Safety-critical' },
];

const PHASE_OPTIONS = [
  { value: '', label: 'All phases' },
  { value: 'observer', label: 'Observer' },
  { value: 'recommendation', label: 'Recommendation' },
  { value: 'supervised', label: 'Supervised' },
  { value: 'full_autonomous', label: 'Full autonomous' },
];

const AGENT_OPTIONS = [
  { value: '', label: 'All agents' },
  { value: 'reliability_engineering', label: 'Reliability Engineering' },
  { value: 'planning_scheduling', label: 'Planning & Scheduling' },
  { value: 'elisa_execution', label: 'Elisa Execution' },
  { value: 'asset_intelligence', label: 'Asset Intelligence' },
  { value: 'supply_chain', label: 'Supply Chain' },
  { value: 'compliance_governance', label: 'Compliance & Governance' },
];

function humanize(s: string): string {
  return s.replaceAll('_', ' ');
}

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

const selectClass =
  'rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-text ' +
  'focus:outline-none focus:ring-2 focus:ring-primary/40';

export default function DecisionInboxPage() {
  const [params, setParams] = useSearchParams();
  const risk = params.get('risk') ?? '';
  const phase = params.get('phase') ?? '';
  const agent = params.get('agent') ?? '';
  const page = Math.max(0, Number(params.get('page') ?? '0') || 0);

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete('page'); // any filter change resets to the first page
    setParams(next, { replace: true });
  };

  const setPage = (nextPage: number) => {
    const next = new URLSearchParams(params);
    if (nextPage > 0) next.set('page', String(nextPage));
    else next.delete('page');
    setParams(next, { replace: true });
  };

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['agentic', 'decisions', { risk, phase, agent, page }],
    queryFn: () =>
      getDecisions({
        risk: (risk || undefined) as RiskClassification | undefined,
        phase: phase || undefined,
        agent_id: agent || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="inbox-loading">
        <Spinner size="lg" label="Loading Decision Inbox" />
      </div>
    );
  }

  if (error) {
    if (isNotEntitled(error)) return <NotEnabled />;
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-8">
        <EmptyState
          variant="error"
          icon={<Icon as={AlertTriangle} size="lg" />}
          title="Couldn't load the Decision Inbox"
          description="Something went wrong while loading decisions. Try again."
          action={<Button onClick={() => void refetch()}>Retry</Button>}
        />
      </div>
    );
  }

  if (!data) return <NotEnabled />;

  const { items, total } = data;
  const start = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const end = Math.min(total, page * PAGE_SIZE + items.length);
  const hasPrev = page > 0;
  const hasNext = end < total;

  return (
    <div className="mx-auto w-full max-w-5xl p-6">
      <Stack gap="lg">
        <PageHeader
          title="Decision Inbox"
          description="Every decision agents have recorded for your tenant. Read-only — open a decision to see its evidence and response history."
        />

        <Section>
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2">
              <Text variant="caption" tone="muted">
                Risk
              </Text>
              <select
                className={selectClass}
                value={risk}
                onChange={e => setFilter('risk', e.target.value)}
                aria-label="Filter by risk level"
              >
                {RISK_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2">
              <Text variant="caption" tone="muted">
                Phase
              </Text>
              <select
                className={selectClass}
                value={phase}
                onChange={e => setFilter('phase', e.target.value)}
                aria-label="Filter by autonomy phase"
              >
                {PHASE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2">
              <Text variant="caption" tone="muted">
                Agent
              </Text>
              <select
                className={selectClass}
                value={agent}
                onChange={e => setFilter('agent', e.target.value)}
                aria-label="Filter by agent"
              >
                {AGENT_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            {isFetching && <Spinner size="sm" label="Updating" />}
          </div>
        </Section>

        <Section>
          {items.length === 0 ? (
            <Card padding="lg">
              <EmptyState
                compact
                icon={<Icon as={Inbox} size="lg" />}
                title="No decisions match these filters"
                description="Try clearing a filter, or wait for agents to record more decisions."
              />
            </Card>
          ) : (
            <Stack gap="sm">
              <Card padding="none">
                <ul className="divide-y divide-border" data-testid="decision-list">
                  {items.map(d => (
                    <li key={d.id}>
                      <Link
                        to={`/decisions/${d.id}`}
                        className="flex flex-wrap items-center gap-3 px-4 py-3 transition-colors hover:bg-elevated"
                      >
                        <div className="min-w-0 flex-1">
                          <Text variant="body-sm" className="font-medium">
                            {humanize(d.decision_type)}
                          </Text>
                          <Text variant="caption" tone="muted">
                            {humanize(d.agent_id)} · {d.autonomy_phase} · {formatWhen(d.created_at)}
                          </Text>
                        </div>
                        <Badge variant={RISK_BADGE[d.risk_classification]} size="sm">
                          {RISK_LABEL[d.risk_classification]}
                        </Badge>
                        {d.latest_response_type && (
                          <Badge variant="info" size="sm">
                            {humanize(d.latest_response_type)}
                          </Badge>
                        )}
                        {d.deadline_state && d.deadline_state !== 'closed' && (
                          <Badge variant="warning" size="sm">
                            {humanize(d.deadline_state)}
                          </Badge>
                        )}
                        <Text variant="mono" tone="muted">
                          {d.calibrated_confidence ?? d.raw_confidence}%
                        </Text>
                        <Icon as={ChevronRight} size="sm" className="text-muted" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </Card>

              <div className="flex items-center justify-between">
                <Text variant="caption" tone="muted">
                  Showing {start}–{end} of {total}
                </Text>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!hasPrev}
                    onClick={() => setPage(page - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!hasNext}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </Stack>
          )}
        </Section>

        <Heading level={2} className="sr-only">
          Decision list
        </Heading>
      </Stack>
    </div>
  );
}
